import { useEffect } from 'react';
import { ASPECT_RATIOS } from '../types';
import { getViewportDimensions, DEFAULT_DIMENSIONS } from '../config/dimensions';

export const useViewportEffects = (
  open: boolean,
  asset: any,
  editorState: any,
  containerRef: React.RefObject<HTMLDivElement>
) => {
  // Track container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        editorState.setContainerDimensions({
          width: rect.width - 16,
          height: rect.height - 16 - 40
        });
      }
    };

    const measureWithDelay = () => {
      updateDimensions();
      setTimeout(updateDimensions, 50);
      setTimeout(updateDimensions, 150);
      setTimeout(updateDimensions, 300);
    };

    measureWithDelay();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [open]);

  // Update viewport dimensions based on aspect ratio and view mode
  useEffect(() => {
    let containerWidth = editorState.containerDimensions.width;
    let containerHeight = editorState.containerDimensions.height;
    
    if (containerWidth === 0 || containerHeight === 0) {
      containerWidth = Math.min(window.innerWidth * 0.9 - 400, 1000);
      containerHeight = Math.min(window.innerHeight * 0.85 - 200, 700);
    }
    
    const aspectRatio = ASPECT_RATIOS[editorState.selectedAspectRatio].ratio;
    const viewMode = editorState.comparisonMode;
    
    const usableWidth = containerWidth - 16;
    const usableHeight = containerHeight - 20;
    
    // Get dimensions from centralized config
    const dimensions = getViewportDimensions(editorState.selectedAspectRatio, viewMode);
    let viewportWidth = dimensions.width;
    let viewportHeight = dimensions.height;
    
    // Dynamic sizing for unknown aspect ratios that don't fit in container
    if (editorState.selectedAspectRatio === 'custom' || 
        (viewMode === 'single' && !['1:1', '16:9', '9:16', '4:3'].includes(editorState.selectedAspectRatio))) {
      if (aspectRatio > (usableWidth / usableHeight)) {
        viewportWidth = usableWidth;
        viewportHeight = Math.round(usableWidth / aspectRatio);
      } else {
        viewportHeight = usableHeight;
        viewportWidth = Math.round(usableHeight * aspectRatio);
      }
    }
    
    editorState.setCropCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));
    
    // DON'T call setZoomCoordinates here - it overrides user pan/zoom coordinates
    // The dynamic setters will handle mode-specific updates through direct state setters below
    
    editorState.setSecondaryCropCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));

    // Update all mode-specific coordinates to ensure consistency
    // when switching between crop and markup modes
    editorState.setPrimaryCropCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));
    
    editorState.setPrimaryMarkupCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));
    
    editorState.setSecondaryCropModeCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));
    
    editorState.setSecondaryMarkupCoordinates((prev: any) => ({
      ...prev,
      viewportWidth,
      viewportHeight
    }));
  }, [editorState.selectedAspectRatio, editorState.containerDimensions, editorState.comparisonMode]);

  // Initialize viewport dimensions when asset first loads (preserve saved coordinates)
  useEffect(() => {
    if (open && asset) {
      const updateViewportDimensions = (coords: any) => {
        return {
          ...coords,
          // Preserve x, y, scale from saved coordinates
          // Only update viewport dimensions if not set
          viewportWidth: coords.viewportWidth || DEFAULT_DIMENSIONS.width,
          viewportHeight: coords.viewportHeight || DEFAULT_DIMENSIONS.height,
        };
      };
      
      editorState.setZoomCoordinates((prev: any) => updateViewportDimensions(prev));
      editorState.setCropCoordinates((prev: any) => updateViewportDimensions(prev));
      editorState.setSecondaryZoomCoordinates((prev: any) => updateViewportDimensions(prev));
      editorState.setSecondaryCropCoordinates((prev: any) => updateViewportDimensions(prev));
    }
  }, [open, asset?.id]);
};